<template>
  <div class="page-content store-checkout">
    <el-dialog
      v-model="status.isModalVisible"
      class="order-submitted"
      title="Order"
    >
      <div v-if="status.errorHtml !== ''">
        <h4>There was an error placing your order</h4>
        Please review the error below:
        <message-html
          :result-html="status.errorHtml"
          :has-top-line="false"
          has-bottom-line
        />
      </div>

      <div v-if="deliveryOrder">
        <h5>Your order was succesfully placed with {{ deliveryOrder.storeOrder.service }}.</h5>
        <p>{{ deliveryOrder.storeOrder.subtitle }} from {{ deliveryOrder.storeOrder.title }}</p>

        <h6>Delivery To:</h6>
        <p>{{ deliveryOrder.storeOrder.estimate.address.formattedAddress }}</p>

        <h6>Delivery Time:</h6>
        <p>{{ deliveryOrder.storeOrder.estimate.timeEstimate.min }} - {{ deliveryOrder.storeOrder.estimate.timeEstimate.max }} min</p>

        <h6>Items Ordered:</h6>
        <ol>
          <li
            v-for="item in deliveryOrder.storeOrder.estimate.items"
            :key="item.title"
          >
            {{ item.item.title }} - ${{ (item.item.price / 100).toFixed(2) }}
          </li>
        </ol>
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button
            type="purple"
            @click="$router.push(getPathForOrder(deliveryOrder.id))"
          >View Order</el-button>
          <el-button
            type="purple"
            @click="$emit('clear-store')"
          >Back to Search</el-button>
          <el-button
            type="purple"
            @click="clearCheckout()"
          >Back to Store</el-button>
        </span>
      </template>
    </el-dialog>

    <template v-if="isOperatorDashboard">
      <p-button
        type="yellow"
        round
        native-type="submit"
        class="mb-3"
        @click="clearCheckout()"
      >
        <i class="nc-icon nc-minimal-left mr-2" />Back
      </p-button>

      <p-button
        type="purple"
        round
        native-type="button"
        class="mb-3"
        @click="$emit('clear-cart')"
      >
        Clear Cart
      </p-button>
    </template>

    <div
      v-if="storeGroup"
      class="row"
    >
      <div class="col-12">
        <div class="card">
          <div
            v-loading="status.isOrderLoading || isEstimateLoading"
            class="card-content py-4"
            element-loading-custom-class="custom-spinner-class"
          >
            <div class="row">
              <div class="col-7 checkout-info">
                <div>
                  <h4 class="my-0">
                    Checkout
                  </h4>
                  <div class="mt-2">
                    <b>Restaurant:</b> {{ storeGroup.title }}
                  </div>
                  <div v-if="estimate.timeEstimate">
                    <b>Delivery Estimate:</b> {{ estimate.timeEstimate.min }} - {{ estimate.timeEstimate.max }} mins
                  </div>
                  <div class="service-name">
                    <b>Service:</b> {{ estimate.service }}
                  </div>
                  <div
                    v-if="isDeliveryEstimate"
                    class="subscription-name"
                  >
                    <b>Plan:</b>
                    <div>
                      <dropdown-list
                        v-model="plan"
                        :options="gourmetEstimatePlanList"
                      />
                    </div>
                  </div>
                </div>
                <span
                  v-if="resolveOrder"
                  class="actions-area"
                >
                  This order will resolve: &nbsp;
                  <router-link
                    :to="getPathForOrder(resolveOrder)"
                    target="_blank"
                  >
                    Order Id: {{ resolveOrder }}
                  </router-link>
                  <br>
                </span>
              </div>
              <div class="col-5">
                <div
                  id="charges"
                  v-loading="isEstimateLoading"
                  element-loading-custom-class="custom-spinner-class"
                >
                  <div
                    v-for="charge in estimate.charges"
                    :key="charge.title"
                    class="row mb-0"
                  >
                    <div class="col-7 charge-title">
                      {{ charge.title }}

                      <el-tooltip
                        v-if="charge.tooltips?.length > 0"
                        placement="bottom"
                      >
                        <template #content>
                          <div id="charge-tooltip-content">
                            <div
                              v-for="tooltip in charge.tooltips"
                              :key="tooltip.title"
                            >
                              <div
                                v-if="tooltip.title"
                                class="tooltip-title"
                              >
                                {{ tooltip.title }}
                              </div>
                              <div
                                class="tooltip-value"
                                v-text="tooltip.value"
                              />
                            </div>
                          </div>
                        </template>
                        <i class="fa fa-info charge-info" />
                      </el-tooltip>
                    </div>
                    <div class="col-5 charge-value">
                      <span v-if="charge.originalValue">${{ (charge.originalValue / 100).toFixed(2) }}</span>
                      ${{ (charge.value / 100).toFixed(2) }}
                    </div>
                  </div>
                  <template v-if="estimate.userCharges">
                    <div class="row mb-0">
                      <el-divider />
                      <div class="col-7 charge-title">
                        GoGo Expiring Credits
                      </div>
                      <div class="col-5 charge-value">
                        ${{ (estimate.userCharges.expiringCredits / 100).toFixed(2) }}
                      </div>
                    </div>
                    <div class="row mb-0">
                      <div class="col-7 charge-title">
                        GoGo Credits
                      </div>
                      <div class="col-5 charge-value">
                        ${{ (estimate.userCharges.credits / 100).toFixed(2) }}
                      </div>
                    </div>
                    <div class="row mb-0">
                      <div class="col-7 charge-title">
                        Credit Card
                      </div>
                      <div class="col-5 charge-value">
                        ${{ (estimate.userCharges.card / 100).toFixed(2) }}
                      </div>
                    </div>
                  </template>
                </div>
                <div class="estimate-description">
                  <strong>Please note:</strong> The estimate total depends on many factors and can change during the submission process
                  within a $2 range.
                </div>
                <button
                  class="estimate-refresh"
                  @click="$emit('refresh-estimate')"
                >
                  <i class="fa fa-refresh" /> Refresh Estimate
                </button>
              </div>
            </div>

            <h6 class="sub-section-title">
              Cart Items
            </h6>
            <div class="table-view">
              <el-table
                :data="cart"
                border
                fit
              >
                <template #empty>
                  Your cart is empty, go back.
                </template>
                <el-table-column
                  v-for="(item, index) in itemsTableColumns"
                  :key="index"
                  :prop="item.field"
                  :label="item.label"
                  :width="item.width"
                  :align="item.isCenterAlign ? 'center' : 'left'"
                  :formatter="item.formatter"
                />

                <el-table-column
                  v-if="isOperatorDashboard"
                  :fixed="true"
                  :width="180"
                  align="center"
                  label=""
                >
                  <template #default="scope">
                    <el-button
                      size="small"
                      :disabled="isEstimateLoading || status.isOrderLoading"
                      @click.stop="$emit('item-options', scope.row, true)"
                    >
                      Edit
                    </el-button>
                    <el-button
                      size="small"
                      :disabled="isEstimateLoading || status.isOrderLoading"
                      @click.stop="$emit('remove-item', scope.row)"
                    >
                      Remove
                    </el-button>
                  </template>
                </el-table-column>
                <el-table-column
                  label="Item Options"
                  width="auto"
                >
                  <template #default="scope">
                    <order-options :options="optionsFormatter(scope.row?.options || [])" />
                    <template v-if="scope?.row?.item?.specialInstructions && scope.row.item.specialInstructions.length">
                      <p class="item-special-instructions">
                        Special Instructions
                      </p>
                      {{ scope.row.item.specialInstructions }}
                    </template>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <customized-form
              id="submitOrder"
              class="mt-20"
              @submit="submitOrderFormSubmit"
              @keydown.enter.prevent=""
            >
              <template v-if="isOperatorDashboard">
                <h6 class="sub-section-title">
                  Additional Contact Info
                </h6>
                <p class="mb-10 notification-text">
                  Do not need to put this info if it's the same with customer number and name
                </p>
                <fieldset>
                  <div class="row align-items-center">
                    <label class="col-sm-3 control-label">Preferred Contact Number</label>
                    <phone-number-input
                      id="gourmet-preferred-phone"
                      v-model="preferredPhoneNumber"
                      class="col-sm-4"
                    />
                  </div>
                  <div class="row align-items-center">
                    <label class="col-sm-3 control-label">Client Name</label>
                    <div class="col-sm-4">
                      <sanitized-input
                        id="name"
                        v-model="clientName"
                      />
                    </div>
                  </div>
                  <div
                    v-if="status.isRedelivery"
                    class="row align-items-center"
                  >
                    <label class="col-sm-3 control-label">Nature of Complaint*</label>
                    <div class="col-sm-4">
                      <dropdown-list
                        v-model="natureOfComplaint"
                        required
                        placeholder="Select Nature of Complaint"
                        :options="gourmetNatureOfComplaintList"
                      />
                    </div>
                  </div>
                  <div
                    v-if="status.isRedelivery"
                    class="row align-items-center"
                  >
                    <label class="col-sm-3 control-label">Original Order ID</label>
                    <div class="col-sm-4">
                      <sanitized-input
                        id="orderId"
                        v-model="originalOrderId"
                        placeholder="Original Order ID"
                      />
                    </div>
                  </div>
                </fieldset>
                <hr>
              </template>
              <h6 class="sub-section-title">
                Courier Tip
              </h6>

              <div
                v-if="isGogoSubsidizingFirstTip"
                class="driver-tip-free-notice"
                role="status"
              >
                <div class="driver-tip-free-notice__label">
                  <strong>Please let the customer know, they don't pay any tip now.</strong>
                </div>
                <p class="driver-tip-free-notice__message">
                  "GoGo is fully covering {{ freeDriverTipPercent }}% tip to the courier on your first order! 
                </p>
                <p class="driver-tip-free-notice__message">
                  How much would you like to tip couriers on future orders?"
                </p>
              </div>
              <fieldset>
                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">Type</label>
                  <div class="col-sm-9">
                    <el-radio-group
                      v-model="defaultTipForm.type"
                      class="driver-tip-type-group"
                      @change="defaultTipChange"
                    >
                      <el-radio label="percentage">
                        Percentage
                      </el-radio>
                      <el-radio label="amount">
                        Amount
                      </el-radio>
                    </el-radio-group>
                  </div>
                </div>
                <div
                  v-if="defaultTipForm.type === 'percentage'"
                  class="row align-items-center"
                >
                  <label class="col-sm-3 control-label">Percentage*</label>
                  <div class="col-sm-9">
                    <el-input
                      v-model="defaultTipForm.percentage"
                      type="number"
                      class="driver-tip-fixed-width-input"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="0–100"
                      @blur="defaultTipChange"
                    >
                      <template #append>
                        %
                      </template>
                    </el-input>
                  </div>
                </div>
                <div
                  v-if="defaultTipForm.type === 'percentage'"
                  class="row align-items-center"
                >
                  <label class="col-sm-3 control-label">Maximum</label>
                  <div class="col-sm-9">
                    <el-input
                      v-model="defaultTipForm.maximum"
                      type="number"
                      class="driver-tip-fixed-width-input"
                      min="0"
                      max="100"
                      step="0.01"
                      @blur="defaultTipChange"
                    >
                      <template #prepend>
                        $
                      </template>
                    </el-input>
                  </div>
                </div>
                <div
                  v-if="defaultTipForm.type === 'amount'"
                  class="row align-items-center"
                >
                  <label class="col-sm-3 control-label">Amount</label>
                  <div class="col-sm-9">
                    <el-input
                      v-model="defaultTipForm.amount"
                      type="number"
                      class="driver-tip-fixed-width-input"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      @blur="defaultTipChange"
                    >
                      <template #prepend>
                        $
                      </template>
                    </el-input>
                  </div>
                </div>
                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">Default for future orders</label>
                  <div class="col-sm-9">
                    <el-checkbox
                      id="defaultTipShouldUpdate"
                      v-model="defaultTipForm.shouldUpdate"
                      @change="defaultTipChange"
                    />
                  </div>
                </div>
                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">
                    <span
                      class="d-flex w-100 flex-wrap align-items-center justify-content-end"
                    >
                      <strong>Total tip now</strong>
                      <el-tooltip
                        :content="tipForCurrentOrderTooltip"
                        placement="top"
                      >
                        <i class="fa fa-info tip-for-current-trip-tooltip-icon" />
                      </el-tooltip>
                    </span>
                  </label>
                  <div class="col-sm-9 bg-pink-500">
                    <el-input
                      :model-value="tip"
                      class="tip-for-current-trip-input driver-tip-fixed-width-input"
                      type="number"
                      disabled
                    >
                      <template #prepend>
                        $
                      </template>
                    </el-input>
                  </div>
                </div>
              </fieldset>
              <template v-if="isOperatorDashboard">
                <h6 class="sub-section-title">
                  Charge Options
                </h6>
                <fieldset>
                  <div class="row align-items-center">
                    <div class="col-sm-8 offset-3">
                      <el-checkbox
                        id="isRedelivery"
                        v-model="status.isRedelivery"
                        :disabled="isChargeOptionsLocked"
                      >
                        Redelivery
                      </el-checkbox>
                    </div>
                  </div>
                </fieldset>
                <fieldset>
                  <div class="row align-items-center">
                    <div class="col-sm-8 offset-3">
                      <el-checkbox
                        id="chargeNow"
                        v-model="status.chargeNow"
                        :disabled="isChargeOptionsLocked"
                      >
                        Charge Now
                      </el-checkbox>
                    </div>
                  </div>
                  <div
                    v-if="status.isRedelivery"
                    class="row align-items-center"
                  >
                    <div class="col-sm-8 offset-3">
                      <el-checkbox
                        id="doNotCharge"
                        v-model="status.doNotCharge"
                        :disabled="isChargeOptionsLocked"
                      >
                        Do Not Charge
                      </el-checkbox>
                    </div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-sm-8 offset-3">
                      <el-checkbox
                        id="noServiceFee"
                        v-model="status.noServiceFee"
                        :disabled="isChargeOptionsLocked"
                      >
                        Do not charge Gourmet fee
                      </el-checkbox>
                    </div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-sm-8 offset-3">
                      <el-checkbox
                        id="applyDiscountedFee"
                        v-model="status.applyDiscountedFee"
                        :disabled="isChargeOptionsLocked"
                      >
                        Apply discounted Gourmet fee
                      </el-checkbox>
                    </div>
                  </div>
                  <div
                    v-if="isReasonRequired"
                    class="row align-items-center"
                  >
                    <label class="col-sm-3 control-label">Reason*</label>
                    <div class="col-sm-7">
                      <sanitized-textarea
                        id="notes-no-charge-gourmet"
                        v-model="noChargeReason"
                        placeholder="Please note the reason for marking the not charge gourmet or not charge service fee."
                        autosize
                        required
                        :disabled="isChargeOptionsLocked"
                      />
                    </div>
                  </div>
                </fieldset>
              </template>
              <hr>
              <h6 class="sub-section-title">
                Total Charge: ${{ estimate.total / 100 }}
              </h6>

              <h6 class="sub-section-title">
                Delivery
              </h6>
              <div
                v-if="isOperatorDashboard && !isDeliveryEstimate"
                class="sections"
              >
                <p-button
                  type="purple"
                  :class="{ active: deliveryType === SINGLE }"
                  @click="deliveryType = SINGLE"
                >
                  Single Delivery
                </p-button>
                <p-button
                  type="purple"
                  :class="{ active: deliveryType === MULTIPLE }"
                  @click="deliveryType = MULTIPLE"
                >
                  Multi-Delivery
                </p-button>
              </div>

              <template v-if="deliveryType === SINGLE">
                <h6 class="sub-section-title">
                  Delivery Address
                </h6>
                <p class="section-notify-text">
                  Please confirm if there is an apartment or unit number.
                </p>
                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">Street Address*</label>
                  <div class="col-sm-5">
                    <sanitized-input
                      v-model="deliveryAddress.name"
                      placeholder="Street"
                      disabled
                    />
                  </div>
                  <label class="col-sm-1 control-label">Addr 2</label>
                  <div class="col-sm-3">
                    <sanitized-input
                      v-model="deliveryAddress.streetAddress2"
                      placeholder="Apt number"
                    />
                  </div>
                </div>

                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">City*</label>
                  <div class="col-sm-4">
                    <sanitized-input
                      v-model="deliveryAddress.city"
                      required
                      disabled
                    />
                  </div>

                  <label class="col-sm-2 control-label">State*</label>
                  <div class="col-sm-3">
                    <sanitized-input
                      v-model="deliveryAddress.state"
                      required
                      disabled
                    />
                  </div>
                </div>

                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">Zip*</label>
                  <div class="col-sm-4">
                    <sanitized-input
                      v-model="deliveryAddress.zip"
                      required
                      disabled
                    />
                  </div>
                </div>

                <h6 class="sub-section-title">
                  Delivery Instructions for Driver
                </h6>
                <p class="section-notify-text">
                  Do NOT repeat the Address, Phone Number, or Drop-off Options in custom notes.
                </p>
                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">
                    Address Instructions
                    <span class="pl-3">📍</span>
                  </label>
                  <div class="col-sm-9">
                    <sanitized-input
                      id="addressInstructions"
                      v-model="addressInstructions"
                    />
                  </div>
                </div>
                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">
                    Drop-off Options*
                    <span class="pl-3">‼️</span>
                  </label>
                  <div class="col-sm-4">
                    <dropdown-list
                      v-model="dropoffOptionId"
                      :options="dropOffTypeList"
                    />
                  </div>
                </div>
                <div class="row align-items-center">
                  <label class="col-sm-3 control-label">
                    Accessibility Instructions
                    <span class="pl-3">♿</span>
                  </label>
                  <div class="col-sm-9">
                    <sanitized-input
                      id="accessibilityInstructions"
                      v-model="accessibilityInstructions"
                    />
                  </div>
                </div>
                <div class="row">
                  <label class="col-sm-3 control-label pt-5">
                    Delivery Instructions
                    <span class="pl-3">📝</span>
                  </label>
                  <div class="col-sm-9">
                    <div
                      v-dompurify-html="utils.convertTextToHtml(deliveryInstructions)"
                      class="delivery-instructions-preview"
                      :class="{'invalid': phoneNumberIncludedInInstructions || addressIncludedInInstructions}"
                    />
                    <p
                      v-if="phoneNumberIncludedInInstructions || addressIncludedInInstructions"
                      class="text-red"
                    >
                      *Please do not repeat the phone number or address. The driver already receives these.
                    </p>
                  </div>
                </div>

                <template v-if="isOperatorDashboard">
                  <h6 class="sub-section-title">
                    Note to LP
                  </h6>
                  <p class="section-notify-text">
                    Internal notes for this order.
                  </p>
                  <div class="row">
                    <label class="col-sm-3 control-label pt-2">
                      Internal Note
                    </label>
                    <div class="col-sm-9">
                      <sanitized-textarea
                        v-model="noteToLP"
                        placeholder="Add internal notes for this order. Example: 'Cx celebrating anniversary.' or 'Cx had issues with past three orders'"
                        autosize
                      />
                    </div>
                  </div>
                </template>
              </template>

              <div v-else>
                <h6 class="mt-5">
                  Addresses
                </h6>
                <el-table
                  :data="deliveryAddresses"
                  border
                  fit
                >
                  <template #empty>
                    No addresses
                  </template>
                  <el-table-column
                    v-for="(item, index) in addressTableColumns"
                    :key="index"
                    :prop="item.field"
                    :label="item.label"
                    :width="item.width"
                    :align="item.isCenterAlign ? 'center' : 'left'"
                    :formatter="item.formatter"
                  />

                  <el-table-column
                    :fixed="true"
                    :width="180"
                    align="center"
                    label=""
                  >
                    <template #default="scope">
                      <div v-if="scope.row?.orderId">
                        <router-link
                          :to="getPathForOrder(scope.row.orderId)"
                          class="btn btn-purple"
                          target="_blank"
                        >
                          View Order
                        </router-link>
                        <div><small>Completed</small></div>
                      </div>
                      <div v-else>
                        <div v-if="!scope.row?.isSubmitting">
                          <el-button
                            size="small"
                            :disabled="status.bulkOrderInProgress"
                            @click="removeAddress(scope.row)"
                          >
                            Remove
                          </el-button>
                          <el-button
                            v-if="scope.row?.errors"
                            size="small"
                            class="btn-yellow"
                            @click="showError(scope.row)"
                          >
                            View Error
                          </el-button>
                          <div
                            v-if="scope.row.wasAttempted"
                            class="error"
                          >
                            <small>InCompleted</small>
                          </div>
                        </div>
                        <div v-else>
                          <i class="el-icon-loading" />
                        </div>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>

                <h6 class="mt-5">
                  Add Address
                </h6>
                <customized-form
                  id="newAddress"
                  class="form-vertical"
                  @submit="addNewAddress"
                >
                  <fieldset>
                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label">Customer Phone*</label>
                      <div class="col-sm-4">
                        <sanitized-input
                          v-model="newAddress.phone"
                          placeholder="Client Phone Number"
                        />
                      </div>
                      <div class="col-sm-2">
                        <el-button
                          type="purple"
                          @click="getCustomerInfo"
                        >
                          Get Customer
                        </el-button>
                      </div>
                    </div>

                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label">Customer Name*</label>
                      <div class="col-sm-4">
                        <sanitized-input
                          v-model="newAddress.name"
                          placeholder="Client Name"
                          disabled
                        />
                      </div>
                    </div>

                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label">Street Address*</label>
                      <div class="col-sm-9">
                        <address-autocomplete-input
                          id="newAddress"
                          ref="newAddress"
                          address-type="Full Address"
                          class="form-control auto-complete"
                          placeholder="Enter a delivery address"
                          :anchor-bound="anchorBound"
                          autocomplete="nope"
                          required
                          :value="newAddress.googleAddress"
                          @place_changed="setDeliveryAddress"
                        />
                        <div
                          v-if="!newAddress.deliveryAddress.placeId"
                          class="error"
                        >
                          Select address from dropdown above
                        </div>
                      </div>
                    </div>

                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label">Address Line 2</label>
                      <div class="col-sm-4">
                        <sanitized-input
                          v-model="newAddress.deliveryAddress.streetAddress2"
                          placeholder="Apt number"
                        />
                      </div>
                    </div>

                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label no-padding-width">Delivery Instructions*</label>
                      <div class="col-sm-9">
                        <sanitized-textarea
                          id="delivery-instructions"
                          v-model="newAddress.deliveryInstructions"
                          placeholder="Your message to the delivery driver"
                          autosize
                        />
                      </div>
                    </div>
                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label no-padding-width">Note to LP</label>
                      <div class="col-sm-9">
                        <sanitized-textarea
                          v-model="newAddress.noteToLP"
                          placeholder="Add internal notes for this order. Example: 'Cx celebrating anniversary.' or 'Cx had issues with past three orders'"
                          autosize
                        />
                      </div>
                    </div>
                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label">Drop-off Options*</label>
                      <div class="col-sm-4">
                        <dropdown-list
                          v-model="newAddress.dropoffOptionId"
                          :options="dropOffTypeList"
                        />
                      </div>
                    </div>
                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label" />
                      <div class="col-sm-4">
                        <p
                          v-if="!status.isPrimaryAccountForBulkOrderLoaded"
                          class="error"
                        >
                          Please load a customer account above
                        </p>
                        <p-button
                          type="purple"
                          native-type="submit"
                          :disabled="!newAddress || !newAddress.deliveryAddress.placeId || !status.isPrimaryAccountForBulkOrderLoaded"
                        >
                          Add
                        </p-button>
                      </div>
                    </div>
                  </fieldset>
                </customized-form>
              </div>

              <h6 class="sub-section-title">
                Price Check
              </h6>
              <fieldset>
                <div class="row align-items-center">
                  <div class="col-sm-3 control-label">
                    <label class="control-label">Allow Price Check</label>
                    <el-popover
                      placement="top-start"
                      :width="400"
                      trigger="hover"
                      popper-class="delivery-detail-popover"
                    >
                      <template #reference>
                        <i
                          class="fa fa-question-circle"
                          style="font-size: 18px; margin-left: 10px"
                        />
                      </template>

                      <template #default>
                        <span>
                          <strong>Allow Price Check</strong> allows our system to check if any of the items added to your cart have a higher
                          price than usual. If prices have changed and you do not select this box, the system will not be able to place the
                          order.
                        </span>
                      </template>
                    </el-popover>
                  </div>
                  <div class="col-sm-9 is-override-block">
                    <el-checkbox
                      v-model="skipPriceCheck"
                      @change="isOverrideEstimateChanged"
                    />
                  </div>
                </div>
              </fieldset>
              <hr>
              <div class="form-submit-button-group">
                <div
                  v-if="deliveryType === MULTIPLE"
                  class="error"
                >
                  <strong>
                    <div v-if="status.bulkOrderInProgress">
                      <i class="el-icon-loading" /> A bulk order is being placed. Do not leave this page, please wait.
                    </div>
                    <div v-else>Warning: Multiple orders will be placed</div>
                  </strong>
                </div>
                <div
                  v-if="estimateFailed"
                  class="error"
                >
                  <strong>
                    Error: The last estimate generation failed. Please click the "Refresh Estimate" button above and/or fix item errors
                    before ordering.
                  </strong>
                </div>

                <div
                  v-if="status.isReEstimateRequired"
                  class="error mb-20"
                >
                  <strong> Error: The order's current total exceeds $2 more than the initial estimate. </strong>
                  <br>
                  <strong> Please click the "Refresh Estimate" button above or enable "Allow Estimate" option. </strong>
                </div>

                <template v-if="isPlaceOrder">
                  <p-button
                    type="yellow"
                    @click="clearCheckout()"
                  >
                    Cancel
                  </p-button>
                  <p-button
                    v-if="deliveryType === SINGLE"
                    type="purple"
                    native-type="submit"
                    :disabled="isSinglePlaceOrderDisabled"
                  >
                    Place Order
                  </p-button>
                  <p-button
                    v-else
                    type="purple"
                    native-type="submit"
                    :disabled="isMultiplePlaceOrderDisabled"
                  >
                    Place Orders ({{ getUnplacedOrdersCount() }})
                  </p-button>
                </template>
                <el-popover
                  v-if="numberOfUnpaidGourmetOrders || numberOfUnpaidRides || numberOfUnpaidGroceries"
                  placement="top-start"
                  width="400"
                  trigger="hover"
                  class="disableButtonQuestionMark"
                  popper-class="delivery-detail-popover"
                  content="The Place Order button is disabled because the current customer has unpaid services"
                >
                  <template #reference>
                    <i class="el-icon-question" />
                  </template>
                </el-popover>
                <template v-if="isOperatorDashboard">
                  <div
                    v-if="isSinglePlaceOrder"
                    class="buttons-info-block"
                  >
                    <p-button
                      type="info"
                      :disabled="isSinglePlaceOrderDisabled"
                      @click="onOpenScheduleModal"
                    >
                      Schedule Order
                    </p-button>
                    <p-button
                      type="info"
                      :disabled="isSaveOrderDisabled"
                      @click="saveOrder"
                    >
                      Save Order
                    </p-button>
                    <p-button
                      v-if="copyOrder && copyOrder.status === 'saved'"
                      type="info"
                      :disabled="isSinglePlaceOrderDisabled"
                      @click="updateOrder(copyOrder.id)"
                    >
                      Update Order
                    </p-button>
                  </div>
                  <div v-if="deliveryType === SINGLE && copyOrder && copyOrder.status === 'scheduled'">
                    <p-button
                      type="info"
                      :disabled="isSinglePlaceOrderDisabled"
                      @click="onOpenScheduleModal"
                    >
                      Schedule Order
                    </p-button>
                    <p-button
                      type="info"
                      :disabled="isSinglePlaceOrderDisabled"
                      @click="updateScheduledOrder(copyOrder.id)"
                    >
                      Update Order
                    </p-button>
                  </div>

                  <div
                    v-if="isDeliveryEstimate"
                    class="save-cart-container"
                  >
                    <p-button
                      native-type="submit"
                      type="purple"
                    >
                      Save Cart
                    </p-button>
                  </div>
                </template>
              </div>
            </customized-form>
          </div>
        </div>
      </div>
    </div>
    <delivery-scheduler
      v-if="storeGroup && estimate"
      v-model:show="status.isScheduledDeliveryOpen"
      :estimate="estimate"
      :customer-info="customerInfo"
      :store-title="storeGroup.title"
      @schedule-delivery="onScheduleDelivery"
    />
  </div>
</template>

<script lang="ts">
import { ref, Ref, reactive, watch, computed, onMounted } from 'vue';
import _, { get } from 'lodash';
import events from '@/events';

import utils from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useAddress } from '@/use/address';
import { useDashboard } from '@/use/app';
import { useGourmet } from '@/use/gourmet';
import { TableColumnType } from '@/models/table';
import { ScheduledDeliveryInput } from '@/models/scheduledDelivery';
import { dropOffTypeList, gourmetNatureOfComplaintList, gourmetEstimatePlanList } from '@/constants/deliveryOrder';
import { scheduledDeliveryService } from '@/services/scheduledDelivery';
import { gourmetService } from '@/services/gourmet';
import { callerService } from '@/services/caller';

import OrderOptions from '@/components/UIComponents/OrderingForms/OrderOptions.vue';
import DeliveryScheduler from '../ScheduledDelivery/DeliveryScheduler.vue';
import { updateCallerTipGourmet } from '@/services/gourmet/updateCallerTipGourmet';
import { abTestService } from '@/services/abTests';
import type { AssignAbTestPerStrategyMutation, DefaultTipInput, DefaultTipType } from '@/gql/graphql';

type AssignAbTestPerStrategyResult = NonNullable<AssignAbTestPerStrategyMutation['assignAbTestPerStrategy']>;

const SINGLE = 'single';
const MULTIPLE = 'multiple';
/** First gourmet order over this total (cents) forces charge-now and locks fee waivers */
const FIRST_GOURMET_HIGH_ORDER_MAX_CENTS = 100 * 100;
const SUBSIDIZE_FIRST_GOURMET_TIP_AB_TEST_NAME = 'subsidize_first_gourmet_tip';
const itemsTableColumns: TableColumnType[] = [
  { label: 'Item Name', field: 'item.title' },
  {
    label: 'Price',
    field: 'item.price',
    formatter: (row: any) => {
      return row?.item?.price && `$${(row.item.price / 100).toFixed(2)}`;
    },
    width: 80,
    isCenterAlign: true,
  },
  { label: 'Quantity', field: 'item.quantity', width: 80, isCenterAlign: true },
];
const addressTableColumns: TableColumnType[] = [
  { label: 'Client Name', field: 'name', width: 150 },
  { label: 'Client Phone', field: 'phone', width: 150, isCenterAlign: true },
  { label: 'Delivery Address', field: 'deliveryAddress.formattedAddress', isCenterAlign: true },
];

export default {
  name: 'StoreCheckout',

  components: {
    DeliveryScheduler,
    OrderOptions,
  },

  props: {
    resolveOrder: {
      type: String,
      default: '',
    },

    storeGroup: {
      type: Object,
      default: () => null,
    },

    cart: {
      type: Array<any>,
      default: () => [],
    },

    deliveryAddress: {
      type: Object,
      default: () => ({
        name: '',
        streetAddress2: '',
        city: '',
        state: '',
        zip: '',
      }),
    },

    additionalCharges: {
      type: Object,
      default: () => ({}),
    },

    defaultTip: {
      type: Object,
      default: () => ({
        type: 'percentage' as 'percentage' | 'amount',
        percentage: 10 as number | undefined,
        maximum: 10 as number | undefined,
        amount: 3 as number | undefined,
        shouldUpdate: true,
      }),
    },

    chargeOptions: {
      type: Object,
      default: () => ({}),
    },

    estimate: {
      type: Object,
      default: () => ({}),
    },

    orderOptions: {
      type: Object,
      default: () => ({}),
    },

    isEstimateLoading: {
      type: Boolean,
      default: false,
    },

    allItemsFromTheSameMenu: {
      type: Boolean,
      default: false,
    },

    estimateFailed: {
      type: Boolean,
      default: false,
    },

    copyOrder: {
      type: Object,
      default: () => null,
    },

    contactInfo: {
      type: Object,
      default: () => ({}),
    },

    planName: {
      type: String,
      default: '',
    },

    isDeliveryEstimate: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'clear-cart',
    'clear-store',
    'refresh-estimate',
    'item-options',
    'remove-item',
    'clear-checkout',
    'change-charge-options',
    'change-plan',
  ],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const { customerInfo, customerFullName, anchorBound, totalOrderInfo, isNeedToShowFirstDeliveryPopup } = useCustomer();

    const { getFormattedAddress } = useAddress();
    const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
    const { isOperatorDashboard } = useDashboard();
    const { setSavedCart } = useGourmet();

    const deliveryOrder: Ref<any> = ref(null);
    const deliveryAddresses: Ref<any> = ref([]);
    const newAddress: Ref<any> = ref({
      phone: '',
      callerId: null,
      name: '',
      deliveryAddress: {},
      googleAddress: '',
      deliveryInstructions: '',
      noteToLP: '',
      dropoffOptionId: dropOffTypeList[0].key,
    });
    const skipPriceCheck: Ref<any> = ref(null);

    const addressInstructions = ref('');
    const accessibilityInstructions = ref('');
    const noteToLP = ref('');
    const dropoffOptionId = ref(dropOffTypeList[0].key);
    const callerId = ref('');
    const subsidizeFirstGourmetTipAbTestAssignment: Ref<AssignAbTestPerStrategyResult | null> = ref(null);
    const isGogoSubsidizingFirstTip = computed(() => isNeedToShowFirstDeliveryPopup.value && subsidizeFirstGourmetTipAbTestAssignment.value?.groupType === 'test');
    const noChargeReason = ref('');

    const freeDriverTipPercent = 15;

    const tipForCurrentOrderTooltip = computed(() => {
      const who = isGogoSubsidizingFirstTip.value ? 'GoGo' : 'customer';
      return `How much tip ${who} will pay for the current order.`;
    });

    const createDefaultTipNoPropState = () => ({
      type: 'percentage' as 'percentage' | 'amount',
      percentage: 10 as number | undefined,
      maximum: 10 as number | undefined,
      amount: 3 as number | undefined,
      shouldUpdate: true,
    });

    /** Mutable form model; `props.defaultTip` is read-only in Vue 3, so v-model cannot bind to it. */
    const defaultTipForm = reactive(createDefaultTipNoPropState());

    const normalizeDefaultTipNumber = (v: any): number | undefined => {
      if (v === '' || v === null || v === undefined) {
        return undefined;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    const applyDefaultTipFromProps = (d: any) => {
      if (d == null) {
        Object.assign(defaultTipForm, createDefaultTipNoPropState());
        return;
      }
      defaultTipForm.type = d?.type === 'amount' ? 'amount' : 'percentage';
      defaultTipForm.percentage = normalizeDefaultTipNumber(d?.percentage);
      defaultTipForm.maximum = normalizeDefaultTipNumber(d?.maximum);
      defaultTipForm.amount = normalizeDefaultTipNumber(d?.amount);
      defaultTipForm.shouldUpdate = d?.shouldUpdate ?? true;
    };

    const estimateSubtotalDollars = computed(() => {
      const charges = props.estimate?.charges as any[];
      const c = charges?.find((ch) => ch.title?.trim() === 'Subtotal');
      const estimateSubtotalCents = c ? Number(c.value) : 0;
      const estimateSubtotalDollars = estimateSubtotalCents / 100;
      return estimateSubtotalDollars
    });

    const tip = computed(() => {
      const subtotalDollars = estimateSubtotalDollars.value;

      if (isGogoSubsidizingFirstTip.value) return freeDriverTipAmountDollars.value;

      let tipResult = 0;

      if (defaultTipForm.type === 'percentage') {
        const pct = normalizeDefaultTipNumber(defaultTipForm.percentage) ?? 0;
        let t = subtotalDollars * (pct / 100);
        const capDollars = normalizeDefaultTipNumber(defaultTipForm.maximum);
        if (capDollars !== undefined) {
          t = Math.min(t, capDollars);
        }

        tipResult = Math.round(t * 100) / 100;
      } else if (defaultTipForm.type === 'amount') {
        const raw = normalizeDefaultTipNumber(defaultTipForm.amount) ?? 0;
        const amt = Math.min(100, Math.max(0, raw));
        tipResult = Math.round(amt * 100) / 100;
      }

      return tipResult;
    });

    const syncTipDollarsToParent = () => {
      // Pushes the computed tip to the parent component,
      // call on blur and when the estimate (subtotal) changes, 
      // not on every input keystroke. 
      props.additionalCharges.tip = tip.value;
      props.additionalCharges.shouldTipBeSubsidized = isGogoSubsidizingFirstTip.value
    };

    const freeDriverTipAmountDollars = computed(() => Math.round(estimateSubtotalDollars.value * freeDriverTipPercent) / 100);

    const updateDefaultTipOnBackend = () => {
      const id = callerId.value;
      if (defaultTipForm.shouldUpdate && id) {
        const input: DefaultTipInput = {
          type: defaultTipForm.type as DefaultTipType,
          percentage: normalizeDefaultTipNumber(defaultTipForm.percentage),
          maximum: normalizeDefaultTipNumber(defaultTipForm.maximum),
          amount: normalizeDefaultTipNumber(defaultTipForm.amount),
        };
        void updateCallerTipGourmet({ callerId: id, defaultTip: input });
      }
    }

    const defaultTipChange = () => {
      let pct = normalizeDefaultTipNumber(defaultTipForm.percentage);
      if (pct !== undefined) {
        pct = Math.min(100, Math.max(0, pct));
        defaultTipForm.percentage = pct;
      } else {
        defaultTipForm.percentage = undefined;
      }
      let max = normalizeDefaultTipNumber(defaultTipForm.maximum);
      if (max !== undefined) {
        max = Math.max(0, max);
        defaultTipForm.maximum = max;
      } else {
        defaultTipForm.maximum = undefined;
      }
      let amt = normalizeDefaultTipNumber(defaultTipForm.amount);
      if (amt !== undefined) {
        amt = Math.min(100, Math.max(0, amt));
        defaultTipForm.amount = amt;
      } else {
        defaultTipForm.amount = undefined;
      }

      syncTipDollarsToParent();
    };

    // TODO: check if this is needed or used and address it
    const setDeliveryAddress = () => {}

    const deliveryType = ref(SINGLE);
    const preferredPhoneNumber = ref('');
    const clientName = ref('');
    const plan = ref(gourmetEstimatePlanList[1].key);
    const originalOrderId = ref(null);
    const natureOfComplaint = ref(null);
    const status = reactive({
      isModalVisible: false,
      isOrderLoading: false,
      isScheduledDeliveryOpen: false,
      isReEstimateRequired: false,
      isPrimaryAccountForBulkOrderLoaded: false,
      noServiceFee: false,
      chargeNow: false,
      doNotCharge: false,
      applyDiscountedFee: false,
      bulkOrderInProgress: false,
      isRedelivery: false,
      errorHtml: '',
    });

    const numberOfUnpaidGourmetOrders = computed(() => get(customerInfo.value, 'unpaidGourmetOrders', []).length);

    const numberOfUnpaidRides = computed(() => get(customerInfo.value, 'unpaidRides', []).length);

    const numberOfUnpaidGroceries = computed(() => get(customerInfo.value, 'unpaidGroceries', []).length);

    const isSaveOrderDisabled = computed(() => {
      return (
        props.isEstimateLoading ||
        props.estimateFailed ||
        !props.allItemsFromTheSameMenu ||
        status.isOrderLoading ||
        status.isReEstimateRequired
      );
    });

    const isSinglePlaceOrderDisabled = computed(() => {
      return (
        props.isEstimateLoading ||
        props.estimateFailed ||
        !props.allItemsFromTheSameMenu ||
        status.isOrderLoading ||
        status.isReEstimateRequired ||
        !!numberOfUnpaidGourmetOrders.value ||
        !!numberOfUnpaidRides.value ||
        !!numberOfUnpaidGroceries.value
      );
    });

    const isMultiplePlaceOrderDisabled = computed(() => {
      return (
        status.bulkOrderInProgress ||
        !getUnplacedOrdersCount() ||
        !!numberOfUnpaidGourmetOrders.value ||
        !!numberOfUnpaidRides.value ||
        !!numberOfUnpaidGroceries.value
      );
    });

    const isReasonRequired = computed(() => {
      return status.noServiceFee || status.applyDiscountedFee || status.doNotCharge;
    });

    const isFirstGourmetHighValueOrder = computed(() => {
      const total = props.estimate?.total;
      const gourmets = totalOrderInfo.value?.totalGourmets || 0;

      if (gourmets > 0) {
        return false;
      }

      if (total >= FIRST_GOURMET_HIGH_ORDER_MAX_CENTS) {
        return true;
      }

      return false;
    });

    const isChargeOptionsLocked = computed(() => isOperatorDashboard.value && isFirstGourmetHighValueOrder.value);

    const isPlaceOrder = computed(() => {
      return (!props.copyOrder || props.copyOrder.status !== 'scheduled') && !props.isDeliveryEstimate;
    });

    const isSinglePlaceOrder = computed(() => {
      return deliveryType.value === SINGLE && isPlaceOrder.value;
    });

    const deliveryInstructions = computed(() => {
      let instructions = '';
      if (addressInstructions.value.trim()) {
        instructions = `📍 ${addressInstructions.value.trim()}\n`;
      }

      instructions += `‼️ ${ utils.getLabelByField(dropOffTypeList, dropoffOptionId.value) }\n`;

      if (accessibilityInstructions.value.trim()) {
        instructions += `♿ ${accessibilityInstructions.value.trim()}\n`;
      }

      return instructions;
    });

    const phoneNumberIncludedInInstructions = computed(() => {
      const normalize = (str: string) => str.replace(/\D/g, ''); // keep digits only

      const normalizedText = normalize(deliveryInstructions.value);
      const normalizedPhone = normalize(customerInfo.value.phoneNumber ?? '');

      return normalizedText.includes(normalizedPhone);
    });

    const addressIncludedInInstructions = computed(() => {
      return ['name', 'city', 'state', 'zip'].some(
        (key) =>
          props.deliveryAddress[key] &&
          deliveryInstructions.value.includes(props.deliveryAddress[key]));
    });

    const clearCheckout = () => {
      emit('clear-checkout');

      deliveryType.value = SINGLE;
      deliveryAddresses.value = [];
      newAddress.value = {
        phone: '',
        callerId: null,
        name: '',
        deliveryAddress: {},
        googleAddress: '',
        deliveryInstructions: '',
        noteToLP: '',
        dropoffOptionId: dropOffTypeList[0].key,
      };
      status.isPrimaryAccountForBulkOrderLoaded = false;
      skipPriceCheck.value = null;
      status.isReEstimateRequired = false;
    };

    const getUnplacedOrdersCount = () => {
      const orders = deliveryAddresses.value.filter((addr: any) => !addr.orderId);
      return orders ? orders.length : 0;
    };

    const showError = (address: any) => {
      if (!address.errors) {
        return;
      }

      showErrorPopupDialog({
        text: address.errors,
      });
    };

    const removeAddress = (address: any) => {
      const index = deliveryAddresses.value.findIndex((item: any) => item === address);
      deliveryAddresses.value.splice(index, 1);
    };

    const addNewAddress = () => {
      if (!newAddress.value || !newAddress.value.deliveryAddress.placeId) {
        return;
      }

      deliveryAddresses.value.push({
        name: newAddress.value.name,
        phone: newAddress.value.phone,
        callerId: newAddress.value.callerId,
        deliveryAddress: newAddress.value.deliveryAddress,
        deliveryInstructions: newAddress.value.deliveryInstructions,
        noteToLP: newAddress.value.noteToLP,
        orderId: null,
        isSubmitting: false,
        wasAttempted: false,
        errors: null,
      });
      newAddress.value = {
        phone: '',
        callerId: null,
        name: '',
        deliveryAddress: {},
        googleAddress: '',
        deliveryInstructions: '',
        dropoffOptionId: dropOffTypeList[0].key,
        noteToLP: '',
      };
      status.isPrimaryAccountForBulkOrderLoaded = false;
    };

    const getCustomerInfo = async () => {
      if (!newAddress.value.phone) {
        return;
      }

      const caller = await callerService.fetchCustomerInfo({
        phoneNumber: newAddress.value.phone,
      });

      newAddress.value.callerId = caller.id;
      (newAddress.value.name = `${caller.firstName} ${caller.lastName}`),
        (newAddress.value.deliveryAddress = {
          streetAddress2: caller.streetAddress2,
        });
      newAddress.value.googleAddress = caller.streetAddress ? getFormattedAddress({
        streetAddress: caller.streetAddress ?? '',
        streetAddress2: caller.streetAddress2 ?? '',
        city: caller.city ?? '',
        state: caller.state ?? '',
        zip: caller.zip ?? '',
        country: caller.country ?? '',
      }) : '';
      newAddress.value.deliveryInstructions = getCustomerNote(caller);
      newAddress.value.dropoffOptionId = getDropoffOptionIdFromCaller(caller);
      status.isPrimaryAccountForBulkOrderLoaded = true;
    };

    const optionsFormatter = (options: any) => {
      return utils.optionsFormatter(options);
    };

    const getCartData = (
      callerId: string,
      estimateData: any,
      orderOptions: {
        storeInstructions?: string;
        deliveryInstructions?: string;
        dropoffOptionId?: string;
        noteToLP?: string;
      },
      {
        orderId,
        resolveOrderId,
        originalOrderId,
        isConfirmedSameStoreActiveOrder,
        skipPriceCheck,
        isRedelivery,
        natureOfComplaint,
        scheduledDelivery,
      }: {
        orderId?: string;
        resolveOrderId?: string;
        originalOrderId?: string | null;
        isConfirmedSameStoreActiveOrder?: boolean;
        skipPriceCheck?: boolean;
        isRedelivery?: boolean;
        natureOfComplaint?: string | null;
        scheduledDelivery?: any;
      } = {},
    ) => {
      const estimate = _.cloneDeep(estimateData);
      clearStateAndStartLoading();
      estimate.address.streetAddress2 = props.deliveryAddress.streetAddress2;
      estimate.chargeOptions = {
        ...(estimate.chargeOptions || {}),
        ...(props.chargeOptions || {}),
        noServiceFee: status.noServiceFee,
        applyDiscountedFee: status.applyDiscountedFee,
        doNotCharge: status.doNotCharge,
        chargeNow: status.chargeNow,
        reason: noChargeReason.value,
      };

      if (isFirstGourmetHighValueOrder.value) {
        estimate.chargeOptions = {
          ...estimate.chargeOptions,
          chargeNow: true,
          doNotCharge: false,
          noServiceFee: false,
          applyDiscountedFee: false,
          reason: '',
        };
      }

      const storeGroup = { ...props.storeGroup };
      storeGroup.stores = get(props.storeGroup, 'stores', []).filter((store: any) => store.service === props.estimate.service);

      const data: any = {
        callerId,
        estimate,
        orderOptions,
        storeGroup,
        storeTitle: props.storeGroup.title,
        clientName: clientName.value ? clientName.value : null,
        orderId,
        resolveOrderId,
        originalOrderId,
        isConfirmedSameStoreActiveOrder,
        skipPriceCheck,
        isRedelivery,
        natureOfComplaint,
        scheduledDelivery,
      };

      if (utils.getValidPhoneNumber(preferredPhoneNumber.value)) {
        data.preferredPhoneNumber = preferredPhoneNumber.value;
      } else {
        data.preferredPhoneNumber = null;
      }

      return data;
    };

    const submitOrderFormSubmit = async () => {
      if (props.isDeliveryEstimate) {
        return onSaveCart();
      }

      defaultTipChange();

      const htmlText = `
        <ul>
          ${
            status.chargeNow
              ? `<li style="text-align: left;">We will charge $${(props.estimate?.total / 100).toFixed(2)} before processing the order</li>`
              : `<li style="text-align: left;">$1 hold for verification purposes</li>
                 <li style="text-align: left; padding-top: 10px;">Call back within 1 hour if there are issues with the order</li>`
          }
          <li style="text-align: left; padding-top: 10px;">The customer should be available for calls until the order has been delivered</li>
          <li style="text-align: left; padding-top: 10px;"><b>Inform customer we cannot cancel order once it's been placed</b></li>
        </ul>
      `;

      const isConfirmed = await showConfirmPopupDialog({
        text: htmlText,
        title: 'Please Remind Customer',
        confirmButtonText: 'Order',
        cancelButtonText: 'Cancel',
        type: 'html',
        icon: 'warning',
      });

      if (!isConfirmed) {
        return;
      }

      updateDefaultTipOnBackend();

      if (deliveryType.value === SINGLE) {
        const response = await submitOrder(callerId.value, props.estimate, props.resolveOrder, {
          storeInstructions: '',
          deliveryInstructions: deliveryInstructions.value,
          dropoffOptionId: dropoffOptionId.value,
          noteToLP: noteToLP.value,
          addressNote: addressInstructions.value,
          accessibilityNote: accessibilityInstructions.value,
        });

        status.isOrderLoading = false;
        const errors = _.get(response, 'errors');
        if (errors) {
          const message = get(errors[0], 'message', '');
          if (message.indexOf('TOTAL_GREATER_THAN_INITIAL_ESTIMATE_TOTAL') !== -1) {
            status.isReEstimateRequired = true;
          }

          showErrorPopupDialog({
            text: message,
          });
        } else if (response) {
          deliveryOrder.value = response;
          status.isModalVisible = true;
        }
      } else {
        status.bulkOrderInProgress = true;

        for (const address of deliveryAddresses.value) {
          if (address.orderId) {
            continue;
          }

          address.isSubmitting = true;
          address.wasAttempted = true;

          const estimate = _.cloneDeep(props.estimate);

          estimate.address = address.deliveryAddress;

          const response = await submitOrder(address.callerId, estimate, null, {
            storeInstructions: '',
            deliveryInstructions: address.deliveryInstructions,
            dropoffOptionId: address.dropoffOptionId,
            noteToLP: address.noteToLP,
          });

          address.isSubmitting = false;
          const errors = _.get(response, 'errors');

          if (errors) {
            address.errors = errors[0].message;
          } else if (response) {
            address.orderId = response.id;
          }
        }

        status.bulkOrderInProgress = false;
      }
    };

    const submitOrder = async (
      callerId: string,
      estimate: any,
      resolveOrderId: any,
      orderOptions: any,
      isConfirmedSameStoreActiveOrder?: any,
    ): Promise<any> => {
      if (!estimate) {
        return null;
      }

      const data = getCartData(callerId, estimate, orderOptions, {
        isConfirmedSameStoreActiveOrder,
        skipPriceCheck: skipPriceCheck.value,
        isRedelivery: status.isRedelivery,
        resolveOrderId,
        originalOrderId: (status.isRedelivery && originalOrderId.value) || null,
        natureOfComplaint: (status.isRedelivery && natureOfComplaint.value) || null,
      });

      if (data.isRedelivery && !data.originalOrderId) {
        const isConfirmed = await showConfirmPopupDialog({
          text: 'Are you sure you want to process re-delivery order without original order',
          title: 'Redelivery Order?',
        });

        if (!isConfirmed) {
          return;
        }
      }

      if (data.isRedelivery && !data.orderOptions?.noServiceFee && !data.orderOptions?.doNotCharge) {
        const isConfirmed = await showConfirmPopupDialog({
          text: 'Are you sure you want to process re-delivery order without "No Service Fee" or "Do Not Charge"',
          title: 'Redelivery Order?',
        });

        if (!isConfirmed) {
          return;
        }
      }

      try {
        const response = await gourmetService.submitOrder(data);

        return response;
      } catch (error: any) {
        const errorText = utils.getGraphqlError(error, false);

        if (errorText.indexOf('SAME_STORE_ORDER') !== -1) {
          const isConfirmed = await showConfirmPopupDialog({
            text: `This customer already has an active order for ${data.storeGroup.title}.<br>Do you still want to place this order?`,
            title: 'ERROR ORDER ALREADY PLACED',
            confirmButtonText: 'Place Order',
            cancelButtonText: 'Cancel',
            type: 'html',
            icon: 'error',
            customClass: 'same-store-confirm',
          });

          if (isConfirmed) {
            status.isOrderLoading = true;
            return submitOrder(callerId, estimate, resolveOrderId, orderOptions, true);
          }

          return;
        }
        showErrorPopupDialog({
          text: errorText,
        });

        status.isOrderLoading = false;

        return {
          errors: error.graphQLErrors,
        };
      }
    };

    const onSaveCart = () => {
      defaultTipChange();
      updateDefaultTipOnBackend();
      const data = getCartData(
        callerId.value,
        props.estimate,
        {
          storeInstructions: '',
          deliveryInstructions: deliveryInstructions.value,
          dropoffOptionId: dropoffOptionId.value,
          noteToLP: noteToLP.value,
        },
        {
          resolveOrderId: props.resolveOrder,
          skipPriceCheck: skipPriceCheck.value,
          isRedelivery: status.isRedelivery,
          originalOrderId: (status.isRedelivery && originalOrderId.value) || null,
          natureOfComplaint: (status.isRedelivery && natureOfComplaint.value) || null,
        },
      );

      setSavedCart(data);

      showSuccessPopupDialog({
        text: 'The cart has been saved successfully. You can place an order with this cart after completing registration. Please note: the cart will be removed if you refresh the browser or visit another page.',
      });
      status.isOrderLoading = false;
    };

    const saveOrder = async (event: any, orderId?: string) => {
      const question = orderId ? 'Update saved Order for account' : 'Save order to account';
      const isSaveOrder = await showConfirmPopupDialog({
        text: `${question}: ${customerFullName.value} (${customerInfo.value.phoneNumber})?`,
        title: 'Save Order',
        confirmButtonText: 'Save',
      });

      if (!isSaveOrder) {
        return;
      }

      if (!props.estimate) {
        return;
      }

      defaultTipChange();
      updateDefaultTipOnBackend();
      const data = getCartData(
        callerId.value,
        props.estimate,
        {
          storeInstructions: '',
          deliveryInstructions: deliveryInstructions.value,
          dropoffOptionId: dropoffOptionId.value,
        },
        {
          orderId,
        },
      );

      try {
        await gourmetService.saveGourmetOrder(data);

        events.emit('refresh-right-panel');

        const respMsg = orderId
          ? 'The saved order has been successfully updated for the account.'
          : 'The order has been successfully saved to the account.';
        showSuccessPopupDialog({
          text: respMsg,
        });
      } catch (error) {
        const errorText = utils.getGraphqlError(error, false);
        const errorTitle = errorText.includes('LIMIT_MAX_SAVED_ORDER') ? 'Delete one of the saved orders' : 'Error!';
        showErrorPopupDialog({
          text: errorText,
          title: errorTitle,
        });
      }
      status.isOrderLoading = false;
    };

    const updateScheduledOrder = async (orderId?: string) => {
      const question = 'Update a Scheduled Order for account';
      const isUpdateScheduledOrder = await showConfirmPopupDialog({
        text: `${question}: ${customerFullName.value} (${customerInfo.value.phoneNumber})?`,
        title: 'Update Scheduled Order',
        confirmButtonText: 'Update',
      });

      if (!isUpdateScheduledOrder) {
        return;
      }

      if (!props.estimate) {
        return;
      }

      defaultTipChange();
      updateDefaultTipOnBackend();
      const data = getCartData(
        callerId.value,
        props.estimate,
        {
          storeInstructions: '',
          deliveryInstructions: deliveryInstructions.value,
          dropoffOptionId: dropoffOptionId.value,
        },
        {
          orderId,
        },
      );

      try {
        await gourmetService.saveScheduledOrder(data);

        events.emit('refresh-right-panel');

        showSuccessPopupDialog({
          text: 'The scheduled order has been successfully updated for the account.',
        });
      } catch (error) {
        const errorText = utils.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isOrderLoading = false;
      }
    };

    const onScheduleDelivery = async (scheduledDelivery: any) => {
      const isScheduleDelivery = await showConfirmPopupDialog({
        text: `Schedule an order for account: ${customerFullName.value} (${customerInfo.value.phoneNumber})?`,
        title: 'Schedule',
        confirmButtonText: 'Schedule',
      });

      if (!isScheduleDelivery) {
        return;
      }

      if (!props.estimate) {
        return;
      }

      defaultTipChange();
      updateDefaultTipOnBackend();
      const data: ScheduledDeliveryInput = getCartData(
        callerId.value,
        props.estimate,
        {
          storeInstructions: '',
          deliveryInstructions: deliveryInstructions.value,
          dropoffOptionId: dropoffOptionId.value,
        },
        {
          scheduledDelivery,
        },
      );

      status.isScheduledDeliveryOpen = false;

      try {
        await scheduledDeliveryService.createScheduledDelivery(data);

        showSuccessPopupDialog({
          text: 'The order has been successfully scheduled for the account.',
        });
      } catch (error) {
        const errorText = utils.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isOrderLoading = false;
      }
    };

    const updateOrder = async (orderId: any) => {
      await saveOrder(null, orderId);
    };

    const getDropoffOptionIdFromCaller = (caller: {
      gourmetNotes?: { dropOffOptionId?: string | null } | null;
      isLeaveAtDoor?: boolean | null;
    }) => {
      return (
        caller.gourmetNotes?.dropOffOptionId ||
        (caller.isLeaveAtDoor ? dropOffTypeList[1].key : dropOffTypeList[0].key)
      );
    };

    const applyGourmetNotesFromCaller = (caller: {
      gourmetNotes?: { addressNote?: string | null; accessibilityNote?: string | null; dropOffOptionId?: string | null } | null;
      isLeaveAtDoor?: boolean | null;
    }) => {
      addressInstructions.value = caller.gourmetNotes?.addressNote ?? '';
      accessibilityInstructions.value = caller.gourmetNotes?.accessibilityNote ?? '';
      dropoffOptionId.value = getDropoffOptionIdFromCaller(caller);
    };

    const updateCallerInfo = () => {
      if ('id' in customerInfo.value) {
        callerId.value = customerInfo.value.id;
      }

      applyGourmetNotesFromCaller(customerInfo.value);
    };

    const getCustomerNote = (caller: any) => {
      return `Customer Name: ${caller.firstName} ${caller.lastName} | Customer Phone: ${formatPhoneNumber(
        caller.phoneNumber,
      )} | If you cannot reach ${caller.firstName}, please call +1 (855) 754-5328`;
    };

    const formatPhoneNumber = (phoneNumberString: string) => {
      const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
      const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

      if (match) {
        var intlCode = match[1] ? '+1 ' : '';
        return `${intlCode}(${match[2]}) ${match[3]}-${match[4]}`;
      }

      return null;
    };

    const isOverrideEstimateChanged = () => {
      status.isReEstimateRequired = false;
    };

    const onOpenScheduleModal = () => {
      status.isScheduledDeliveryOpen = true;
    };

    const clearStateAndStartLoading = () => {
      status.isOrderLoading = true;
      status.errorHtml = '';
      deliveryOrder.value = null;
    };

    watch(
      () => props.defaultTip,
      (d: any) => {
        if (d) {
          applyDefaultTipFromProps(d);
          syncTipDollarsToParent();
        }
      },
    );

    watch(callerId, (id, prev) => {
      if (id && (prev === '' || prev == null) && defaultTipForm.shouldUpdate) {
        defaultTipChange();
      }
    });

    watch(
      () => props.estimate,
      () => {
        status.isReEstimateRequired = false;
        syncTipDollarsToParent();
      },
      {
        deep: true,
      },
    );

    watch(
      () => props.contactInfo,
      (newValue: any) => {
        if (newValue) {
          (preferredPhoneNumber.value = newValue.preferredPhoneNumber || ''), (clientName.value = newValue.clientName || '');
        }
      },
    );

    watch(
      () => props.estimate.total,
      () => {
        status.isReEstimateRequired = false;
      },
      {
        deep: true,
      },
    );

    watch(
      () => props.orderOptions,
      (newValue: any) => {
        dropoffOptionId.value = newValue?.dropoffOptionId || dropOffTypeList[0].key;
      },
      {
        deep: true,
      },
    );

    const emitChargeOptions = () => {
      emit('change-charge-options', {
        noServiceFee: status.noServiceFee,
        applyDiscountedFee: status.applyDiscountedFee,
        doNotCharge: status.doNotCharge,
        chargeNow: status.chargeNow,
        reason: noChargeReason.value,
      });
    };

    const applyLockedFirstGourmetChargeOptions = () => {
      if (!isFirstGourmetHighValueOrder.value) {
        return;
      }
      status.chargeNow = true;
      status.doNotCharge = false;
      status.noServiceFee = false;
      status.applyDiscountedFee = false;
      noChargeReason.value = '';
    };

    const getPathForOrder = (id: string) => {
      if (isOperatorDashboard.value) {
        return `/operator/delivery-order-history?gourmetOrderId=${id}#gourmet-history`;
      }

      return `/partner/delivery-orders/${id}`;
    };

    watch(
      isFirstGourmetHighValueOrder,
      (locked) => {
        if (locked) {
          applyLockedFirstGourmetChargeOptions();
        }
      },
      { immediate: true },
    );

    watch(
      () => status.isRedelivery,
      (value: boolean) => {
        if (!value) {
          status.doNotCharge = false;
        }
        emitChargeOptions();
      },
      { immediate: true },
    );

    watch(
      () => status.chargeNow,
      (value: boolean) => {
        if (value) {
          status.doNotCharge = false;
        }
      },
    );

    watch(
      () => status.doNotCharge,
      (value: boolean) => {
        if (value) {
          status.chargeNow = false;
          status.noServiceFee = false;
          status.applyDiscountedFee = false;
        }
        emitChargeOptions();
      },
    );

    watch(
      () => status.applyDiscountedFee,
      (value: boolean) => {
        if (value) {
          status.noServiceFee = false;
          status.doNotCharge = false;
        }
        emitChargeOptions();
      },
    );

    watch(
      () => status.noServiceFee,
      (value: boolean) => {
        if (value) {
          status.applyDiscountedFee = false;
          status.doNotCharge = false;
        }
        emitChargeOptions();
      },
    );

    watch(noChargeReason, emitChargeOptions);

    watch(customerInfo, updateCallerInfo, {
      immediate: true,
    });

    watch(plan, (value: string) => {
      emit('change-plan', value);
    });

    onMounted(() => {
      plan.value = props.planName;
      if (props.defaultTip != null) {
        applyDefaultTipFromProps(props.defaultTip);
      }
      defaultTipChange();
      status.chargeNow = props.chargeOptions.chargeNow ?? false;
      status.doNotCharge = props.chargeOptions.doNotCharge ?? false;
      if (!status.isRedelivery) {
        status.doNotCharge = false;
      }
      status.noServiceFee = props.chargeOptions.noServiceFee;
      status.applyDiscountedFee = props.chargeOptions.applyDiscountedFee;
      noChargeReason.value = props.chargeOptions.reason;
      (preferredPhoneNumber.value = props.contactInfo.preferredPhoneNumber || ''), (clientName.value = props.contactInfo.clientName || '');
      deliveryType.value = SINGLE;
      deliveryAddresses.value = [];
      newAddress.value = {
        phone: '',
        callerId: null,
        name: '',
        deliveryAddress: {},
        googleAddress: '',
        deliveryInstructions: '',
        storeInstructions: '',
        dropoffOptionId: dropOffTypeList[0].key,
      };
      applyLockedFirstGourmetChargeOptions();
    });

    const subsidizeFirstGourmetAbTestKey = computed(() => {
      if (!isNeedToShowFirstDeliveryPopup.value) return null; // not a first gourmet order
      return callerId.value; // callerId is a key for watch
    });

    watch(
      subsidizeFirstGourmetAbTestKey,
      (id) => {
        if (id == null) {
          subsidizeFirstGourmetTipAbTestAssignment.value = null;
          return;
        }
        void abTestService
          .assignAbTestPerStrategy({
            abTestName: SUBSIDIZE_FIRST_GOURMET_TIP_AB_TEST_NAME,
            associatedObjectId: id,
            associatedObjectType: 'caller',
          })
          .then((result) => {
            if (subsidizeFirstGourmetAbTestKey.value !== id) {
              return;
            }
            subsidizeFirstGourmetTipAbTestAssignment.value = result ?? null;
          })
          .catch(() => {
            if (subsidizeFirstGourmetAbTestKey.value !== id) {
              return;
            }
            subsidizeFirstGourmetTipAbTestAssignment.value = null;
          });
      },
      { immediate: true },
    );

    return {
      SINGLE,
      MULTIPLE,
      itemsTableColumns,
      addressTableColumns,
      dropOffTypeList,
      gourmetEstimatePlanList,
      customerInfo,
      isNeedToShowFirstDeliveryPopup,
      subsidizeFirstGourmetTipAbTestAssignment,
      isGogoSubsidizingFirstTip,
      freeDriverTipPercent,
      freeDriverTipAmountDollars,
      tipForCurrentOrderTooltip,
      deliveryOrder,
      deliveryAddresses,
      newAddress,
      skipPriceCheck,
      deliveryInstructions,
      dropoffOptionId,
      noteToLP,
      noChargeReason,
      plan,
      tip,
      defaultTipForm,
      defaultTipChange,
      deliveryType,
      status,
      anchorBound,
      numberOfUnpaidGourmetOrders,
      numberOfUnpaidRides,
      numberOfUnpaidGroceries,
      isSaveOrderDisabled,
      isPlaceOrder,
      isSinglePlaceOrder,
      isSinglePlaceOrderDisabled,
      isMultiplePlaceOrderDisabled,
      isReasonRequired,
      clearCheckout,
      getUnplacedOrdersCount,
      showError,
      removeAddress,
      addNewAddress,
      getCustomerInfo,
      optionsFormatter,
      submitOrderFormSubmit,
      saveOrder,
      updateScheduledOrder,
      onScheduleDelivery,
      updateOrder,
      isOverrideEstimateChanged,
      onOpenScheduleModal,
      setDeliveryAddress,
      preferredPhoneNumber,
      clientName,
      natureOfComplaint,
      originalOrderId,
      gourmetNatureOfComplaintList,
      isOperatorDashboard,
      onSaveCart,
      isChargeOptionsLocked,
      addressInstructions,
      accessibilityInstructions,
      utils,
      phoneNumberIncludedInInstructions,
      addressIncludedInInstructions,
      getPathForOrder,
    };
  },
};
</script>

<style lang="scss">
.tip-for-current-trip-input {
  border: unset;
  .el-input__wrapper {
    background-color: unset !important;
  }
}

.tip-for-current-trip-tooltip-icon {
  margin-left: 6px;
  color: rgb(37, 36, 34);
  border: 2px solid rgb(37, 36, 34);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  cursor: help;
}

.same-store-confirm {
  .swal2-contentwrapper {
    .swal2-title {
      font-size: 25px;
      color: red;
    }
  }
}
.page-content .store-checkout {
  .notification-text {
    color: red;
  }
  .el-divider {
    margin: 5px !important;
  }
  .error {
    color: red;
  }

  .driver-tip-type-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
    align-items: center;
  }

  .driver-tip-fixed-width-input {
    min-width: 150px;
    max-width: 150px;
  }

  .driver-tip-first-order-note {
    margin: 4px 0 10px;
    font-size: 14px;
    color: #333;
  }

  .driver-tip-free-notice {
    background: #fff9c4;
    border-radius: 4px;
    padding: 14px 16px;
    margin-bottom: 16px;

    &__message {
      margin: 0 0 4px;
      font-size: 14px;
      line-height: 1.4;
      color: #1a1a1a;
      white-space: pre-line;
      font-style: italic;
    }

    &__footer {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    &__label {
      margin: 0 0 12px;
      flex: 1;
      min-width: 200px;
    }
  }

  .driver-tip-row-wrapper {
    border: solid 1px red;
  }

  .el-loading-mask {
    left: -2px;
    right: -2px;
    top: -2px;
    bottom: -2px;
  }

  button {
    min-width: 0 !important;
  }

  .card-content {
    overflow: hidden;
    .checkout-info {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .actions-area {
        border: 2px solid #ffa500;
        padding: 7px;
      }
    }
    .disableButtonQuestionMark {
      position: relative;
      bottom: 7px;
      left: 8px;
    }
    .buttons-info-block {
      padding: 10px 16px;
    }
    .item-special-instructions {
      font-size: 16px;
      text-decoration: underline;
    }
  }

  #charges {
    border: 1px solid #ccc;
    padding: 10px;
    border-radius: 2px;

    .charge-title {
      display: flex;
      gap: 10px;
      align-items: center;

      .charge-info {
        color: rgb(37, 36, 34);
        border: 2px solid rgb(37, 36, 34);
        width: 16px;
        height: 16px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
      }
    }
  }

  .service-name {
    text-transform: capitalize;
  }

  .subscription-name {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;

    & > div {
      width: 150px;
    }
  }

  .is-override-block {
    .el-radio__label {
      position: relative;
      top: 5px;
    }
    .error {
      position: absolute;
      top: 19px;
    }
  }
  .el-icon-question {
    font-size: 16px;
    position: relative;
    right: 3px;
    top: 1px;
  }
  .estimate-description {
    border: 0.5px solid #ccc;
    border-top: none;
    padding: 10px;
  }
  .estimate-refresh {
    width: 100%;
    background-color: #6cd097;
    border: none;
    padding: 6px;
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    color: white;
    font-size: 14px !important;
    &:hover {
      cursor: pointer;
      background-color: #45c47d;
    }
  }

  .charge-value {
    text-align: right;

    span {
      text-decoration: line-through;
      color: gray;
    }
  }

  .delivery-instructions {
    background-color: #fcf2b6 !important;
  }

  .save-cart-container {
    margin-top: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .section-notify-text {
    font-style: italic;
    padding-bottom: 20px;
  }

  .delivery-instructions-preview {
    background-color: #fff8e1;
    border: 1px solid #dcdcdc;
    padding: 15px;
    border-radius: 8px;

    &.invalid {
      border-color: #f00;
    }
  }
}

#tooltip-content {
  width: 200px;

  .charge-title {
    text-align: right;
  }
}

#charge-tooltip-content {
  max-width: 250px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 5px;

  .tooltip-title {
    font-size: 15px;
    font-weight: bold;
  }

  .tooltip-value {
    font-size: 14px;
  }
}
</style>
